/**
 * Lectura uniforme de las respuestas del backend para los servicios que
 * usan `fetch` directo.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * El backend no responde con una sola forma de error:
 *
 *   - Las rutas de negocio devuelven  { error: "..." }
 *   - El middleware de sesión devuelve { message: "Token inválido o expirado" }
 *   - El proxy inverso, cuando el backend está caído, devuelve HTML
 *
 * Los servicios leían únicamente `data.error`. Resultado: cualquier fallo de
 * sesión o de servidor se mostraba como un mensaje genérico sin ninguna pista
 * ("Ocurrió un error con la encuesta"), que es justo lo que hacía imposible
 * saber que lo que pasaba era que la sesión llevaba más de 8 horas abierta.
 *
 * Esta función lee las tres formas, y además traduce el 401 a un mensaje que
 * sí le dice a la persona qué hacer.
 */

/** Marca en el error si el problema fue de sesión, para poder reaccionar. */
export class ErrorDeApi extends Error {
  constructor(mensaje, { status, sesionExpirada = false } = {}) {
    super(mensaje);
    this.name = 'ErrorDeApi';
    this.status = status;
    this.sesionExpirada = sesionExpirada;
  }
}

const MENSAJE_SESION = 'Tu sesión expiró. Vuelve a iniciar sesión para continuar.';

/**
 * Convierte una respuesta de `fetch` en datos, o lanza un ErrorDeApi con el
 * mensaje real que mandó el servidor.
 *
 * @param {Response} res         respuesta de fetch
 * @param {string}   mensajeBase texto a usar solo si el servidor no mandó ninguno
 */
export async function leerRespuesta(res, mensajeBase = 'No se pudo completar la operación') {
  const texto = await res.text().catch(() => '');

  let datos = null;
  try {
    datos = texto ? JSON.parse(texto) : null;
  } catch {
    datos = null; // el servidor respondió algo que no es JSON (HTML del proxy, por ejemplo)
  }

  if (res.ok) return datos ?? {};

  // Sesión vencida o ausente: es el caso más frecuente y el que más confunde,
  // porque el resto de la pantalla sigue mostrando datos ya cargados.
  if (res.status === 401) {
    throw new ErrorDeApi(MENSAJE_SESION, { status: 401, sesionExpirada: true });
  }

  const mensajeServidor = datos?.error || datos?.message;
  if (mensajeServidor) {
    throw new ErrorDeApi(mensajeServidor, { status: res.status });
  }

  // Sin cuerpo útil: al menos se dice el código, que es lo que permite
  // distinguir "el servidor está caído" de "escribiste algo mal".
  throw new ErrorDeApi(`${mensajeBase} (error ${res.status})`, { status: res.status });
}

/**
 * Envoltorio de `fetch` que además convierte un fallo de red en un mensaje
 * entendible. Sin esto, si el backend no responde, el navegador lanza
 * "Failed to fetch", que no le dice nada a nadie.
 */
export async function pedir(url, opciones, mensajeBase) {
  let res;
  try {
    res = await fetch(url, opciones);
  } catch {
    throw new ErrorDeApi(
      'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.',
      { status: 0 }
    );
  }
  return leerRespuesta(res, mensajeBase);
}
